// apps/client/src/pages/leads/edm/components/ContactFormModal.tsx
//
// Add / edit one eDM contact. Add requires HOW the person agreed (a fixed
// list), WHEN (a date, default today, never in the future), a note for
// "Other" only, AND the express-consent confirmation tick (founder decision
// 27 Oct 2026). The server composes the consent wording from these, hashes
// it, and stores it with the capturing user and time as the manual consent
// record. Edit sends changed fields only ('' clears a field); the email
// address is the contact's identity and is never editable.
//
// Tags: pick from the registry, or type a new name and create it in place.
// Creation goes through the registry (edmContactAction saveTag, createOnly),
// so the server's name rules and RBAC apply and an existing entry is never
// changed; the new tag is then selected.

import React, { useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import Modal from '../../../../components/common/Modal';
import { DatePickerField } from '../../../../components/common/DatePickerField';
import {
  EDM_CONSENT_METHOD_OPTIONS,
  EDM_CONSENT_NOTE_MAX,
  edmContactErrorMessage,
  type AddContactInput,
  type EdmBrokerLabel,
  type EdmConsentMethod,
  type EdmContact,
  type EdmTagEntry,
  type UpdateContactInput,
} from '../../../../hooks/useEdmContacts';
import { EDM_UI } from '../../../../utils/edm';
import { EDM_MAX_CONTACT_TAGS } from './TagPicker';

export const EXPRESS_CONSENT_CONFIRMATION =
  'I confirm this person gave express consent to receive marketing email';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Server registry key rule (edmContacts.tagNameNorm): trim + lowercase, 1..60 of [a-z0-9 _&'-]. */
const TAG_NAME_RE = /^[a-z0-9 _&'-]{1,60}$/;
export function tagNameNorm(raw: string): string | null {
  const v = raw.trim().toLowerCase();
  if (!TAG_NAME_RE.test(v) || /^__.*__$/.test(v)) return null;
  return v;
}

/** The viewer's own calendar day, 'YYYY-MM-DD' (the date input's value format). */
export function localToday(now: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}

const TAG_NAME_RULE = "Tag names use letters, numbers, spaces and - _ & ' only (up to 60 characters).";

interface Props {
  show: boolean;
  /** null = add a new contact; otherwise edit this one. */
  contact: EdmContact | null;
  tags: EdmTagEntry[];
  brokers: EdmBrokerLabel[];
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onAdd: (input: AddContactInput) => void;
  onUpdate: (input: UpdateContactInput) => void;
  /** Creates (or finds) a registry tag; resolves to its stored name. */
  onCreateTag: (name: string) => Promise<string>;
}

interface FormState {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  tags: string[];
  brokerId: string;
  consentMethod: EdmConsentMethod | '';
  consentAgreedAt: string;
  consentNote: string;
  consentConfirmed: boolean;
}

function initialState(contact: EdmContact | null, today: string): FormState {
  return {
    email: contact?.email ?? '',
    firstName: contact?.firstName ?? '',
    lastName: contact?.lastName ?? '',
    phone: contact?.phone ?? '',
    tags: contact?.tags ?? [],
    brokerId: contact?.brokerId ?? '',
    consentMethod: '',
    consentAgreedAt: today,
    consentNote: '',
    consentConfirmed: false,
  };
}

const sameSet = (a: string[], b: string[]) => a.length === b.length && a.every((x) => b.includes(x));

const ContactFormModalBody: React.FC<Props> = ({
  contact,
  tags,
  brokers,
  busy,
  error,
  onClose,
  onAdd,
  onUpdate,
  onCreateTag,
}) => {
  const isAdd = contact === null;
  const [today] = useState(() => localToday());
  const [form, setForm] = useState<FormState>(() => initialState(contact, today));
  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm((f) => ({ ...f, [k]: v }));
  const [newTag, setNewTag] = useState('');
  const [created, setCreated] = useState<string[]>([]);
  const [tagBusy, setTagBusy] = useState(false);
  const [tagError, setTagError] = useState<string | null>(null);

  // Registry tags, tags created here, and any tag already on this contact,
  // so editing never silently drops a tag the picker would not otherwise list.
  const tagOptions = useMemo(() => {
    const names = new Set(tags.map((t) => t.name));
    created.forEach((t) => names.add(t));
    (contact?.tags ?? []).forEach((t) => names.add(t));
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [tags, contact, created]);

  const emailOk = EMAIL_RE.test(form.email.trim());
  const isOther = form.consentMethod === 'other';
  const noteLen = form.consentNote.trim().length;
  const dateOk = /^\d{4}-\d{2}-\d{2}$/.test(form.consentAgreedAt) && form.consentAgreedAt <= today;
  const consentOk =
    form.consentMethod !== '' && dateOk && (!isOther || (noteLen > 0 && noteLen <= EDM_CONSENT_NOTE_MAX));
  const atTagCap = form.tags.length >= EDM_MAX_CONTACT_TAGS;

  const changes = useMemo<UpdateContactInput | null>(() => {
    if (!contact) return null;
    const out: UpdateContactInput = { contactId: contact.id };
    const text = (k: 'firstName' | 'lastName' | 'phone') => {
      const next = form[k].trim();
      if (next !== (contact[k] ?? '')) out[k] = next;
    };
    text('firstName');
    text('lastName');
    text('phone');
    if (!sameSet(form.tags, contact.tags ?? [])) out.tags = form.tags;
    if (form.brokerId !== (contact.brokerId ?? '')) out.brokerId = form.brokerId;
    return out;
  }, [contact, form]);

  const hasChanges = changes !== null && Object.keys(changes).length > 1;
  const canSubmit = !busy && !tagBusy && (isAdd ? emailOk && consentOk && form.consentConfirmed : hasChanges);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    if (isAdd) {
      if (form.consentMethod === '') return;
      const input: AddContactInput = {
        email: form.email.trim(),
        consentMethod: form.consentMethod,
        consentAgreedAt: form.consentAgreedAt,
      };
      if (isOther) input.consentNote = form.consentNote.trim();
      if (form.firstName.trim()) input.firstName = form.firstName.trim();
      if (form.lastName.trim()) input.lastName = form.lastName.trim();
      if (form.phone.trim()) input.phone = form.phone.trim();
      if (form.tags.length > 0) input.tags = form.tags;
      if (form.brokerId) input.brokerId = form.brokerId;
      onAdd(input);
    } else if (changes) {
      onUpdate(changes);
    }
  };

  const toggleTag = (name: string) => {
    if (form.tags.includes(name)) set('tags', form.tags.filter((t) => t !== name));
    else if (!atTagCap) set('tags', [...form.tags, name]);
  };

  const selectTag = (name: string) => setForm((f) => (f.tags.includes(name) ? f : { ...f, tags: [...f.tags, name] }));

  const createTag = async () => {
    setTagError(null);
    const norm = tagNameNorm(newTag);
    if (!norm) {
      setTagError(TAG_NAME_RULE);
      return;
    }
    if (form.tags.includes(norm)) {
      setNewTag('');
      return;
    }
    if (atTagCap) {
      setTagError(`A contact can have up to ${EDM_MAX_CONTACT_TAGS} tags.`);
      return;
    }
    if (tagOptions.includes(norm)) {
      selectTag(norm);
      setNewTag('');
      return;
    }
    setTagBusy(true);
    try {
      const name = await onCreateTag(norm);
      setCreated((c) => (c.includes(name) ? c : [...c, name]));
      selectTag(name);
      setNewTag('');
    } catch (err) {
      setTagError(edmContactErrorMessage(err));
    } finally {
      setTagBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" aria-label={isAdd ? 'Add contact' : 'Edit contact'}>
      <div>
        <label className={EDM_UI.label} htmlFor="edm-contact-email">
          Email
        </label>
        <input
          id="edm-contact-email"
          type="email"
          className={EDM_UI.input}
          value={form.email}
          disabled={!isAdd}
          onChange={(e) => set('email', e.target.value)}
        />
        {!isAdd && <p className="mt-1 text-xs text-gray-400">The email address cannot be changed.</p>}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className={EDM_UI.label} htmlFor="edm-contact-first">
            First name
          </label>
          <input
            id="edm-contact-first"
            className={EDM_UI.input}
            value={form.firstName}
            onChange={(e) => set('firstName', e.target.value)}
          />
        </div>
        <div>
          <label className={EDM_UI.label} htmlFor="edm-contact-last">
            Last name
          </label>
          <input
            id="edm-contact-last"
            className={EDM_UI.input}
            value={form.lastName}
            onChange={(e) => set('lastName', e.target.value)}
          />
        </div>
        <div>
          <label className={EDM_UI.label} htmlFor="edm-contact-phone">
            Phone
          </label>
          <input
            id="edm-contact-phone"
            className={EDM_UI.input}
            value={form.phone}
            onChange={(e) => set('phone', e.target.value)}
          />
        </div>
        <div>
          <label className={EDM_UI.label} htmlFor="edm-contact-broker">
            Broker
          </label>
          <select
            id="edm-contact-broker"
            className={EDM_UI.input}
            value={form.brokerId}
            onChange={(e) => set('brokerId', e.target.value)}
          >
            <option value="">No broker</option>
            {brokers.map((b) => (
              <option key={b.id} value={b.id}>
                {b.displayName}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <span className={EDM_UI.label}>Tags</span>
        {tagOptions.length === 0 ? (
          <p className="text-xs text-gray-400">No tags yet. Type a name below to create one.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {tagOptions.map((name) => {
              const on = form.tags.includes(name);
              return (
                <button
                  key={name}
                  type="button"
                  aria-pressed={on}
                  disabled={!on && atTagCap}
                  onClick={() => toggleTag(name)}
                  className={`${EDM_UI.chip} ${
                    on ? 'border-blue-500 bg-blue-500/10 text-blue-200' : 'border-gray-600 text-gray-300'
                  }`}
                >
                  {name}
                </button>
              );
            })}
          </div>
        )}
        <div className="mt-2 flex gap-2">
          <input
            id="edm-contact-new-tag"
            aria-label="New tag name"
            className={EDM_UI.input}
            placeholder="Type a tag name, then Create tag"
            maxLength={60}
            value={newTag}
            disabled={tagBusy}
            onChange={(e) => {
              setNewTag(e.target.value);
              setTagError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void createTag();
              }
            }}
          />
          <button
            type="button"
            className={`${EDM_UI.secondaryButton} shrink-0`}
            disabled={tagBusy || newTag.trim() === ''}
            onClick={() => void createTag()}
          >
            <Plus className="h-3.5 w-3.5" /> Create tag
          </button>
        </div>
        {tagError && (
          <p role="alert" className="mt-1 text-xs text-red-300">
            {tagError}
          </p>
        )}
        {atTagCap && (
          <p className="mt-1 text-xs text-amber-300">A contact can have up to {EDM_MAX_CONTACT_TAGS} tags.</p>
        )}
      </div>

      {isAdd && (
        <div className="space-y-3 rounded-lg border border-gray-700 p-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={EDM_UI.label} htmlFor="edm-contact-consent-method">
                How did they agree? (required)
              </label>
              <select
                id="edm-contact-consent-method"
                className={EDM_UI.input}
                value={form.consentMethod}
                onChange={(e) => set('consentMethod', e.target.value as EdmConsentMethod | '')}
              >
                <option value="">Choose one</option>
                {EDM_CONSENT_METHOD_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <DatePickerField
                id="edm-contact-consent-date"
                label="When did they agree?"
                value={form.consentAgreedAt || null}
                disableFuture
                showYearDropdown
                fromYear={2000}
                toYear={Number(today.slice(0, 4))}
                onChange={(v) => set('consentAgreedAt', v)}
              />
              {form.consentAgreedAt === '' && <p className="mt-1 text-xs text-red-300">Choose the date they agreed.</p>}
              {form.consentAgreedAt > today && (
                <p className="mt-1 text-xs text-red-300">The date cannot be in the future.</p>
              )}
            </div>
          </div>
          {isOther && (
            <div>
              <label className={EDM_UI.label} htmlFor="edm-contact-consent-note">
                Describe how they agreed (required)
              </label>
              <textarea
                id="edm-contact-consent-note"
                className={EDM_UI.input}
                rows={2}
                maxLength={EDM_CONSENT_NOTE_MAX}
                placeholder="For example: replied yes to our letter"
                value={form.consentNote}
                onChange={(e) => set('consentNote', e.target.value)}
              />
            </div>
          )}
          <p className="text-xs text-gray-400">Stored as the consent record for this contact.</p>
          <label className="flex items-start gap-2 text-sm text-gray-200">
            <input
              type="checkbox"
              className="mt-1"
              checked={form.consentConfirmed}
              onChange={(e) => set('consentConfirmed', e.target.checked)}
            />
            <span>{EXPRESS_CONSENT_CONFIRMATION}</span>
          </label>
        </div>
      )}

      {error && <div className={EDM_UI.errorBanner}>{error}</div>}

      <div className="flex justify-end gap-2">
        <button type="button" className={EDM_UI.secondaryButton} onClick={onClose}>
          Cancel
        </button>
        <button type="submit" className={EDM_UI.primaryButton} disabled={!canSubmit}>
          {isAdd ? 'Add contact' : 'Save changes'}
        </button>
      </div>
    </form>
  );
};

/** The body is keyed on the contact so each open starts from a fresh form. */
const ContactFormModal: React.FC<Props> = (props) => (
  <Modal title={props.contact ? 'Edit contact' : 'Add contact'} show={props.show} onClose={props.onClose}>
    {props.show && <ContactFormModalBody key={props.contact?.id ?? 'new'} {...props} />}
  </Modal>
);

export default ContactFormModal;
